import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, Loader2 } from "lucide-react";
import { api, assetUrl, formatApiError } from "@/lib/api";
import { ZONES, customToSummary, officialToSummary } from "@/lib/decks";
import { ManaCost } from "@/components/common/ManaCost";
import { SCHEME_SUBTYPES } from "@/lib/schemes";
import { EMPTY_FILTERS, SearchFilters, activeCount, toCustomParams, toScryfall } from "@/components/decks/SearchFilters";
import { IssueBadge, officialStatus } from "@/components/decks/LegalityBadge";

function useCardSearch(tab, q, scope, sub, filters, formatKey) {
  const scry = tab === "official" ? toScryfall(q, filters, formatKey) : "";
  return useQuery({
    queryKey: ["deck-search", tab, q, scope, sub, filters, formatKey],
    enabled: tab === "custom" || scry.length > 1,
    retry: false,
    queryFn: async () => {
      if (tab === "official") return (await api.get("/official/cards/search", { params: { q: scry } })).data.items.map(officialToSummary);
      const url = scope === "mine" ? "/me/cards" : "/cards";
      return (await api.get(url, { params: { ...toCustomParams(q, filters), page_size: 40, scheme_subtype: sub || undefined } })).data.items.map(customToSummary);
    },
  });
}

export function CardSearchPanel({ pool, onAdd, zones = ZONES, formatKey = null, formatName = "" }) {
  const tabs = [pool !== "official_only" && "custom", pool !== "custom_only" && "official"].filter(Boolean);
  const [tab, setTab] = useState(tabs[0]);
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [zone, setZone] = useState("main");
  const [sub, setSub] = useState("");
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [applied, setApplied] = useState({ ...EMPTY_FILTERS });
  const [showFilters, setShowFilters] = useState(false);
  const active = tabs.includes(tab) ? tab : tabs[0];
  const { data = [], isFetching, error } = useCardSearch(active, q, scope, active === "custom" ? sub : "", applied, formatKey);
  const ready = active === "custom" || q.length > 1 || activeCount(applied) > 0;

  return (
    <section className="panel flex max-h-[80vh] flex-col p-4" data-testid="deck-search-panel">
      <div className="mb-3 flex gap-1 rounded-md bg-[#0A1222] p-1">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} aria-pressed={active === t} data-testid={`deck-search-tab-${t}`}
            className={`flex-1 rounded px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${active === t ? "bg-amber-600/30 text-amber-100" : "text-slate-400 hover:text-aether-200"}`}>
            {t === "custom" ? "Custom" : "Official (Scryfall)"}
          </button>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); setQ(input.trim()); setApplied(filters); }} className="flex gap-2">
        <input className="field" placeholder={active === "official" ? "Card name (or Scryfall syntax)" : "Name, type or text"} value={input}
          onChange={(e) => setInput(e.target.value)} aria-label="Search cards to add" data-testid="deck-search-input" />
        <button className="btn-outline px-3" aria-label="Search" data-testid="deck-search-submit"><Search className="h-4 w-4" /></button>
      </form>
      <SearchFilters f={filters} onChange={setFilters} official={active === "official"} formatKey={formatKey} formatName={formatName}
        open={showFilters} onToggle={() => setShowFilters(!showFilters)} />
      {showFilters && <button type="button" className="btn-gold mt-2 w-full py-1.5 text-xs" onClick={() => { setQ(input.trim()); setApplied(filters); }} data-testid="deck-search-apply"><Search className="h-3.5 w-3.5" />Search with filters</button>}
      <div className="mt-3 grid grid-cols-2 gap-2">
        {active === "custom" && (
          <select className="field py-1.5 text-xs" value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Custom card scope" data-testid="deck-search-scope">
            <option value="all">All public cards</option><option value="mine">My cards</option>
          </select>
        )}
        <select className="field py-1.5 text-xs" value={zone} onChange={(e) => setZone(e.target.value)} aria-label="Add to zone" data-testid="deck-search-zone">
          {zones.map((z) => <option key={z.key} value={z.key}>{`Add to: ${z.label}`}</option>)}
        </select>
        {active === "custom" && (
          <select className="field col-span-2 py-1.5 text-xs" value={sub} onChange={(e) => setSub(e.target.value)} aria-label="Scheme subtype filter" data-testid="deck-search-scheme-subtype">
            <option value="">Any card (no scheme filter)</option>
            {Object.entries(SCHEME_SUBTYPES).map(([k, v]) => <option key={k} value={k}>{`Schemes: ${v}`}</option>)}
          </select>
        )}
      </div>
      <div className="mt-3 flex-1 space-y-1.5 overflow-y-auto pr-1" data-testid="deck-search-results">
        {isFetching && <p className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" />Searching…</p>}
        {error && <p className="text-xs text-red-300" data-testid="deck-search-error">{formatApiError(error)}</p>}
        {!isFetching && !error && !ready && <p className="text-xs text-slate-500">Type at least 2 characters or set a filter, then search.</p>}
        {!isFetching && data.length === 0 && ready && !error && <p className="text-xs text-slate-500">No cards found.</p>}
        {data.map((c) => {
          const status = c.source === "official" ? officialStatus(c, formatKey) : null;
          return (
          <div key={`${c.source}-${c.id}`} className="flex items-center gap-2 rounded-md border border-transparent p-1.5 hover:border-[#2A3447] hover:bg-white/[0.03]">
            {c.image_url ? <img src={assetUrl(c.image_url)} alt="" className="h-12 w-9 shrink-0 rounded object-cover" loading="lazy" /> : <div className="h-12 w-9 rounded bg-slate-800" />}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-slate-100">{c.name}</p>
              <p className="truncate text-[11px] text-slate-500">{c.type_line}{c.scheme_subtype && c.scheme_subtype !== "standard" && <span className="ml-1 text-aether-300" data-testid={`deck-search-subtype-${c.id}`}>· {SCHEME_SUBTYPES[c.scheme_subtype]}</span>}</p>
              {status && <div className="mt-0.5"><IssueBadge issue={status} testid={`deck-search-legality-${c.id}`} /></div>}
            </div>
            <ManaCost cost={c.mana_cost} />
            <button className="btn-ghost px-2 text-aether-300" onClick={() => onAdd(c, c.type_line?.includes("Token") && zone === "main" ? "tokens" : zone)}
              aria-label={`Add ${c.name}`} data-testid={`deck-add-${c.source}-${c.id}`}><Plus className="h-4 w-4" /></button>
          </div>
          );
        })}
      </div>
      {active === "official" && <p className="mt-3 border-t border-[#2A3447] pt-2 text-[10px] text-slate-500">Official card data &amp; images via Scryfall · © Wizards of the Coast</p>}
    </section>
  );
}
